import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CheckCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPACE_OPTIONS = [
  "600 - 1,100 sq ft",
  "1,100 - 2,500 sq ft",
  "2,500 - 3,500 sq ft",
  "3,500 - 5,500 sq ft",
  "5,500 - 7,500 sq ft",
  "Multiple units / Custom",
];

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    business_activity: "",
    space_requirement: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        ...form,
        source_page: window.location.href,
      });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" data-testid="lead-form-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <CheckCircle className="h-16 w-16 text-[#D49A38] mx-auto mb-6" />
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#182649] tracking-tight leading-[1.1] uppercase"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="form-success-headline"
          >
            Thank you for your enquiry
          </h2>
          <p
            className="mt-4 text-base md:text-lg text-[#4A5568] leading-relaxed"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="form-success-text"
          >
            Our team will contact you shortly to discuss available options.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-[#D49A38] text-white hover:bg-[#c08a2e] rounded-full px-8 py-6 text-base font-semibold transition-all duration-300 shadow-lg"
              data-testid="form-success-cta-primary"
            >
              <a href="https://1141bc.com/units" target="_blank" rel="noopener noreferrer">
                Explore Available Units
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>

            <Button
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="border-[#182649] text-[#182649] hover:bg-[#182649] hover:text-white rounded-full px-8 py-6 text-base font-medium transition-all duration-300"
              data-testid="form-success-cta-secondary"
            >
              Stay on this page
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" data-testid="lead-form-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left copy */}
          <div className="flex flex-col justify-center">
            <span
              className="text-xs font-semibold tracking-[0.2em] uppercase text-[#D49A38] block"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="form-overline"
            >
              Get Started
            </span>
            <div className="w-12 h-[2px] bg-[#D49A38] mt-4 mb-6" />
            <h2
              className="mt-0 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#182649] tracking-tight leading-[1.1] uppercase"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="form-headline"
            >
              Get availability and <span className="text-[#D49A38]">next steps</span>
            </h2>
            <p
              className="mt-6 text-base md:text-lg text-[#4A5568] leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="form-description"
            >
              Share your requirements and our team will provide tailored availability, pricing, and project details.
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="space-y-5"
            data-testid="lead-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#182649] font-medium text-sm" data-testid="label-name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Full name"
                  className="h-12 rounded-md border-[#E2E8F0] focus-visible:ring-[#3C75B6] text-[#0A1021]"
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-[#182649] font-medium text-sm" data-testid="label-company">Company</Label>
                <Input
                  id="company"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  required
                  placeholder="Company name"
                  className="h-12 rounded-md border-[#E2E8F0] focus-visible:ring-[#3C75B6] text-[#0A1021]"
                  data-testid="input-company"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#182649] font-medium text-sm" data-testid="label-email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@company.com"
                  className="h-12 rounded-md border-[#E2E8F0] focus-visible:ring-[#3C75B6] text-[#0A1021]"
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#182649] font-medium text-sm" data-testid="label-phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="+1 (000) 000-0000"
                  className="h-12 rounded-md border-[#E2E8F0] focus-visible:ring-[#3C75B6] text-[#0A1021]"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_activity" className="text-[#182649] font-medium text-sm" data-testid="label-business-activity">Business Activity</Label>
              <Textarea
                id="business_activity"
                name="business_activity"
                value={form.business_activity}
                onChange={handleChange}
                required
                placeholder="Describe your business activity"
                rows={3}
                className="rounded-md border-[#E2E8F0] focus-visible:ring-[#3C75B6] text-[#0A1021]"
                data-testid="input-business-activity"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#182649] font-medium text-sm" data-testid="label-space-requirement">Space Requirement</Label>
              <Select
                value={form.space_requirement}
                onValueChange={(val) => setForm((prev) => ({ ...prev, space_requirement: val }))}
                required
              >
                <SelectTrigger
                  className="h-12 rounded-md border-[#E2E8F0] focus:ring-[#3C75B6] text-[#0A1021]"
                  data-testid="select-space-requirement"
                >
                  <SelectValue placeholder="Select space requirement" />
                </SelectTrigger>
                <SelectContent data-testid="select-space-options">
                  {SPACE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt} data-testid={`space-option-${opt.toLowerCase().replace(/[\s,/]+/g, "-")}`}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <p className="text-red-600 text-sm" data-testid="form-error">{error}</p>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-[#182649] font-medium text-sm" data-testid="label-notes">Additional Notes <span className="text-[#4A5568] font-normal">(optional)</span></Label>
              <Textarea
                id="notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional information or questions"
                rows={2}
                className="rounded-md border-[#E2E8F0] focus-visible:ring-[#3C75B6] text-[#0A1021]"
                data-testid="input-notes"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#182649] text-white hover:bg-[#3C75B6] rounded-full px-8 py-6 text-base font-semibold transition-all duration-300 mt-2"
              data-testid="form-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Request details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
